import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '@/modules/tenant/services/tenantApi';
import type { DocType } from '../docTypeHeuristic';

/**
 * DOC-03 — the Documents-tab data layer (React Query).
 *
 * Wraps the 45-03 auth-gated document CRUD routes:
 *   GET    boats/{id}/documents            (index, optional ?doc_type filter)
 *   POST   boats/{id}/documents            ("add more" upload — multipart)
 *   PATCH  boats/{id}/documents/{mediaId}  (change type / edit details)
 *   DELETE boats/{id}/documents/{mediaId}  (soft-delete — stamps deleted_at)
 *   POST   boats/{id}/documents/{mediaId}/restore (Undo a soft-delete)
 *   GET    boats/{id}/documents/{mediaId}/download (auth-gated stream)
 *
 * Replace re-uses the same delete + upload routes (soft-delete the old row,
 * upload the new file carrying the prior doc_type) — the backend exposes no
 * dedicated replace verb, so the mutation composes the two.
 *
 * Download is NOT a React Query mutation: it's a direct browser navigation to
 * the auth-gated stream (the session cookie authorizes it server-side per
 * request — the client never holds a raw MinIO URL, threat T-45-06b).
 */

/** A document row as returned by InventoryDocumentController::formatDocuments. */
export interface BoatDocument {
  id: number;
  file_name: string;
  mime_type: string;
  size: number;
  doc_type: DocType | null;
  issued_date: string | null;
  issuer: string | null;
  notes: string | null;
  created_at: string | null;
  /** Auth-gated download route (the `view` policy runs per request). */
  download_url: string;
}

interface DocumentsIndexResponse {
  documents: BoatDocument[];
}

/** Editable metadata for the update (Change type / Edit details) mutation. */
export interface DocumentMetadataPatch {
  doc_type?: DocType | null;
  issued_date?: string | null;
  issuer?: string | null;
  notes?: string | null;
}

export const documentKeys = {
  all: ['boat-documents'] as const,
  lists: () => [...documentKeys.all, 'list'] as const,
  /** Per-boat, per-filter list — a doc_type filter is part of the cache key. */
  list: (boatId: string, docType?: DocType | 'all') =>
    [...documentKeys.lists(), boatId, docType ?? 'all'] as const,
};

/* -------------------------------------------------------------------------- */
/* Service calls                                                              */
/* -------------------------------------------------------------------------- */

async function fetchDocuments(
  boatId: string,
  docType?: DocType | 'all',
): Promise<BoatDocument[]> {
  const params =
    docType && docType !== 'all' ? { doc_type: docType } : undefined;
  const data = await tenantApi.getOne<DocumentsIndexResponse>(
    `/boats/${boatId}/documents`,
    { params },
  );
  return data.documents;
}

async function uploadDocuments(
  boatId: string,
  files: File[],
  docType?: DocType,
): Promise<void> {
  const formData = new FormData();
  for (const file of files) {
    formData.append('media[]', file);
  }
  if (docType) {
    formData.append('doc_type', docType);
  }
  await tenantApi.post(`/boats/${boatId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
}

async function updateDocument(
  boatId: string,
  mediaId: number,
  patch: DocumentMetadataPatch,
): Promise<void> {
  await tenantApi.patch(`/boats/${boatId}/documents/${mediaId}`, patch);
}

async function destroyDocument(boatId: string, mediaId: number): Promise<void> {
  await tenantApi.delete(`/boats/${boatId}/documents/${mediaId}`);
}

async function restoreDocument(boatId: string, mediaId: number): Promise<void> {
  await tenantApi.post(`/boats/${boatId}/documents/${mediaId}/restore`);
}

async function replaceDocument(
  boatId: string,
  mediaId: number,
  file: File,
  docType?: DocType | null,
): Promise<void> {
  // No dedicated replace verb. Upload the replacement FIRST, then soft-delete
  // the old row only after the new file is safely stored — so a failure
  // mid-replace leaves the original intact instead of deleting it with no
  // replacement in place (WR-04). The new file carries the prior doc_type so
  // the replacement keeps its grouping.
  await uploadDocuments(boatId, [file], docType ?? undefined);
  await destroyDocument(boatId, mediaId);
}

/* -------------------------------------------------------------------------- */
/* Hook                                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The Documents-tab data hook. `docType` is the active filter ("all" or a
 * single taxonomy value); it is part of the query key so switching the filter
 * fetches (and caches) the narrowed list. Every mutation invalidates the boat's
 * whole document list family so the count Badge + all filtered views refresh.
 */
export function useDocuments(boatId: string | null, docType: DocType | 'all' = 'all') {
  const queryClient = useQueryClient();

  const invalidate = () => {
    if (!boatId) return;
    // Invalidate every filtered list for this boat (and the count source).
    queryClient.invalidateQueries({ queryKey: documentKeys.lists() });
  };

  const list = useQuery({
    queryKey: documentKeys.list(boatId ?? '', docType),
    queryFn: () => fetchDocuments(boatId!, docType),
    enabled: !!boatId,
  });

  const upload = useMutation({
    mutationFn: ({ files, docType: dt }: { files: File[]; docType?: DocType }) =>
      uploadDocuments(boatId!, files, dt),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: ({
      mediaId,
      patch,
    }: {
      mediaId: number;
      patch: DocumentMetadataPatch;
    }) => updateDocument(boatId!, mediaId, patch),
    onSuccess: invalidate,
  });

  const destroy = useMutation({
    mutationFn: ({ mediaId }: { mediaId: number }) =>
      destroyDocument(boatId!, mediaId),
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: ({ mediaId }: { mediaId: number }) =>
      restoreDocument(boatId!, mediaId),
    onSuccess: invalidate,
  });

  const replace = useMutation({
    mutationFn: ({
      mediaId,
      file,
      docType: dt,
    }: {
      mediaId: number;
      file: File;
      docType?: DocType | null;
    }) => replaceDocument(boatId!, mediaId, file, dt),
    onSuccess: invalidate,
  });

  return {
    /** The filtered document list query (documents, isLoading, isError, …). */
    list,
    upload,
    update,
    destroy,
    restore,
    replace,
    /** Force-refresh the whole document family (e.g. after a combined upload). */
    invalidate,
  };
}
